/**
 * TabBar — Premium top navigation strip that sits flush under the church
 * header. Renders as a single unified band with per-tab accent colours,
 * an animated active-tab underline, and a clean shadow shelf.
 *
 * Same component renders desktop + mobile (single source of truth for tabs).
 * Overflow-x scroll on mobile keeps every tab reachable in one tap.
 */
import React from 'react';
import { motion, LayoutGroup } from 'framer-motion';
import {
  Home,
  Calendar,
  Music,
  BookOpenText,
  Handshake,
  HandCoins,
  MonitorPlay,
  Settings,
} from 'lucide-react';

const TAB_DEFS = [
  { key: 'home',    icon: Home,         label: 'Home',       accent: 'from-sky-500 to-blue-500',       glow: 'shadow-sky-400/40' },
  { key: 'events',  icon: Calendar,     label: 'Events',     accent: 'from-amber-500 to-orange-500',   glow: 'shadow-amber-400/40' },
  { key: 'sermons', icon: BookOpenText, label: 'Sermons',    accent: 'from-emerald-500 to-teal-500',   glow: 'shadow-emerald-400/40' },
  { key: 'choir',   icon: Music,        label: 'Choir',      accent: 'from-purple-500 to-fuchsia-500', glow: 'shadow-fuchsia-400/40' },
  { key: 'teams',   icon: Handshake,    label: 'Ministries', accent: 'from-rose-500 to-pink-500',      glow: 'shadow-rose-400/40' },
  { key: 'give',    icon: HandCoins,    label: 'Give',       accent: 'from-lime-500 to-green-500',     glow: 'shadow-lime-400/40' },
  { key: 'webcast', icon: MonitorPlay,  label: 'Webcast',    accent: 'from-red-500 to-rose-500',       glow: 'shadow-red-400/40' },
];

const TabBar = ({ current, setCurrent, isAuthenticated }) => {
  const tabs = [...TAB_DEFS];
  if (isAuthenticated) {
    tabs.push({ key: 'admin', icon: Settings, label: 'Admin', accent: 'from-slate-700 to-slate-900', glow: 'shadow-slate-500/40' });
  }

  return (
    <nav
      className="fixed top-[160px] sm:top-[96px] left-0 right-0 z-40 bg-white/85 backdrop-blur-2xl border-b border-purple-100 shadow-[0_6px_24px_-12px_rgba(88,28,135,0.35)]"
      data-testid="top-nav-shelf"
    >
      <div className="max-w-7xl mx-auto">
        <LayoutGroup id="top-nav">
          <div
            className="flex items-stretch gap-1 sm:gap-2 px-3 sm:px-6 py-2 overflow-x-auto no-scrollbar snap-x snap-mandatory sm:snap-none sm:justify-center"
          >
            {tabs.map((tab, index) => {
              const active = current === tab.key;
              const Icon = tab.icon;
              return (
                <motion.button
                  key={tab.key}
                  type="button"
                  onClick={() => setCurrent(tab.key)}
                  data-testid={`top-nav-${tab.key}`}
                  className={`relative shrink-0 snap-start group flex items-center gap-2 rounded-full px-4 sm:px-5 py-2 transition-colors duration-200 ${
                    active ? 'text-white' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03, duration: 0.25 }}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.96 }}
                >
                  {/* Active pill — animates between tabs with shared layout */}
                  {active && (
                    <motion.span
                      layoutId="top-nav-pill"
                      className={`absolute inset-0 rounded-full bg-gradient-to-r ${tab.accent} shadow-lg ${tab.glow}`}
                      transition={{ type: 'spring', stiffness: 500, damping: 34 }}
                      aria-hidden="true"
                    />
                  )}

                  {/* Icon chip with per-tab accent when inactive */}
                  <span
                    className={`relative flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                      active
                        ? 'bg-white/25'
                        : `bg-gradient-to-br ${tab.accent} text-white shadow-sm`
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </span>

                  <span
                    className={`relative text-sm font-semibold whitespace-nowrap tracking-tight ${
                      active ? '' : 'group-hover:translate-x-[1px] transition-transform'
                    }`}
                  >
                    {tab.label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </LayoutGroup>
      </div>

      {/* Subtle gold thread reflecting the header's brand accent */}
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" />
    </nav>
  );
};

export default TabBar;
